import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Roles.css';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRolePermissions, setSelectedRolePermissions] = useState([]);

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesResp, permsResp] = await Promise.all([
        api.getRoles(),
        api.getPermissions(),
      ]);
      setRoles(rolesResp.data || []);
      setPermissions(permsResp.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load roles and permissions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await api.updateRole(editingRole.id, roleForm);
      } else {
        await api.createRole(roleForm);
      }
      setShowRoleModal(false);
      resetForm();
      loadData();
    } catch (err) {
      setError('Failed to save role');
    }
  };

  const handleEditPermissions = async (role) => {
    try {
      const response = await api.getRolePermissions(role.id);
      const permIds = response.data.map((p) => p.id);
      setSelectedRolePermissions(permIds);
      setEditingRole(role);
      setShowRoleModal(true);
    } catch (err) {
      setError('Failed to load role permissions');
    }
  };

  const handleSavePermissions = async () => {
    try {
      await api.assignPermissionsToRole(editingRole.id, selectedRolePermissions);
      alert('Permissions updated successfully');
      setShowRoleModal(false);
      resetForm();
    } catch (err) {
      setError('Failed to save permissions');
    }
  };

  const resetForm = () => {
    setRoleForm({ name: '', description: '' });
    setEditingRole(null);
    setSelectedRolePermissions([]);
  };

  const togglePermission = (permId) => {
    setSelectedRolePermissions((prev) =>
      prev.includes(permId)
        ? prev.filter((id) => id !== permId)
        : [...prev, permId]
    );
  };

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const [resource] = perm.name.split('.');
    if (!acc[resource]) acc[resource] = [];
    acc[resource].push(perm);
    return acc;
  }, {});

  return (
    <div className="content-container">
      <div className="page-header-section">
        <h2 className="section-title">Role Management</h2>
        <button onClick={() => setShowRoleModal(true)} className="btn btn-primary">
          Create Role
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading roles...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Role Name</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>{role.name}</td>
                  <td>{role.description || '-'}</td>
                  <td className="actions-cell">
                    <button
                      onClick={() => handleEditPermissions(role)}
                      className="btn btn-sm btn-secondary"
                    >
                      Manage Permissions
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showRoleModal && (
        <div className="modal" onClick={() => setShowRoleModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingRole
                  ? `Manage Permissions for ${editingRole.name}`
                  : 'Create New Role'}
              </h3>
              <button onClick={() => setShowRoleModal(false)} className="close-btn">
                &times;
              </button>
            </div>

            {editingRole ? (
              <div className="permissions-grid">
                {Object.entries(groupedPermissions).map(([resource, perms]) => (
                  <div key={resource} className="permission-group">
                    <h4>{resource.charAt(0).toUpperCase() + resource.slice(1)}</h4>
                    {perms.map((perm) => (
                      <label key={perm.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedRolePermissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                        />
                        {perm.name}
                      </label>
                    ))}
                  </div>
                ))}
                <div className="modal-actions">
                  <button
                    onClick={() => setShowRoleModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button onClick={handleSavePermissions} className="btn btn-primary">
                    Save Permissions
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRoleSubmit}>
                <div className="form-group">
                  <label>Role Name *</label>
                  <input
                    type="text"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, description: e.target.value })
                    }
                    rows="3"
                  />
                </div>
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowRoleModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Create
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Roles;

